import { useUser } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import EmptyAvatar from '@/assets/icons/empty-avatar.svg?react';
import UnsavedChangesDialog from '@/components/dialogs/UnsavedChangesDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { BackgroundTab } from './BackgroundTab';
import { FinancialTab } from './FinancialTab';
import { MyProfileTab } from './MyProfileTab';

type TabValue = 'general' | 'financial' | 'background';

const TAB_OPTIONS: { value: TabValue; label: string }[] = [
  { value: 'general', label: 'General' },
  // { value: 'financial', label: 'Financial' },
  // { value: 'background', label: 'Background' },
];

const DEFAULT_TAB: TabValue = 'general';

const DashboardProfile = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedTab, setSelectedTab] = useState<TabValue>(DEFAULT_TAB);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingTab, setPendingTab] = useState<TabValue | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const newDefaultTab = (queryParams.get('tab') as TabValue) || DEFAULT_TAB;
    setSelectedTab(newDefaultTab);
  }, [location]);

  const handleClickTab = (value: TabValue): void => {
    if (isDirty) {
      setPendingTab(value);
      setShowConfirmDialog(true);
      return;
    }
    setSelectedTab(value);
    navigate(`/dashboard/profile?tab=${value}`);
  };

  const handleConfirmTabSwitch = async (): Promise<void> => {
    setShowConfirmDialog(false);
    // Discard changes: do not save, simply navigate
    if (pendingTab) {
      setIsDirty(false);
      setSelectedTab(pendingTab);
      navigate(`/dashboard/profile?tab=${pendingTab}`);
      setPendingTab(null);
    }
  };

  const handleCancelTabSwitch = (): void => {
    setShowConfirmDialog(false);
  };

  const renderUserAvatar = () => {
    if (user?.imageUrl) {
      return (
        <img
          src={user.imageUrl}
          alt={user.fullName || 'User avatar'}
          className="h-[90px] w-[90px] rounded-full border-2 border-white shadow-lg"
        />
      );
    }
    return (
      <EmptyAvatar className="h-[90px] w-[90px] rounded-full border-2 border-white shadow-lg" />
    );
  };

  const isAddressValid =
    (user?.publicMetadata?.address &&
      user?.publicMetadata?.city &&
      user?.publicMetadata?.state &&
      user?.publicMetadata?.zip_code) ||
    false;

  return (
    <div className="space-y-4F">
      <div className="flex flex-col justify-center md:flex-row md:justify-between">
        <div className="flex flex-col items-center gap-5 md:flex-row">
          {renderUserAvatar()}
          <div className="space-y-1 max-md:text-center">
            <h2 className="as-strong">{user?.fullName || 'No name'}</h2>
            <p className="as-body-02 as-strong text-muted-foreground">
              {isAddressValid &&
                `${user?.publicMetadata?.address}, ${user?.publicMetadata?.city}, ${user?.publicMetadata?.state}, ${user?.publicMetadata?.zip_code}`}
            </p>
          </div>
        </div>
      </div>
      <Tabs
        value={selectedTab}
        onValueChange={(value) => handleClickTab(value as TabValue)}
      >
        <TabsList className="my-4 w-full md:w-fit">
          {TAB_OPTIONS.map(({ value, label }) => (
            <TabsTrigger key={value} value={value} className="w-full">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value="general"
          className="rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <MyProfileTab setIsDirty={setIsDirty} />
        </TabsContent>
        <TabsContent
          value="financial"
          className="rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <FinancialTab setIsDirty={setIsDirty} />
        </TabsContent>
        <TabsContent
          value="background"
          className="rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <BackgroundTab setIsDirty={setIsDirty} />
        </TabsContent>
      </Tabs>

      <UnsavedChangesDialog
        open={showConfirmDialog}
        onConfirm={handleConfirmTabSwitch}
        onCancel={handleCancelTabSwitch}
      />
    </div>
  );
};

export { DashboardProfile };
